import axios from '../utils/setupAxiosAuth';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';

export type InvestorMonthlyMetric = {
  month: string;
  new_customers: number;
  total_customers_cumulative: number;
  paying_customers: number;
  repeat_paying_buyers: number;
  repeat_buyer_rate_pct: number;
  order_count: number;
  gmv_inr: number;
  volume_mt: number;
  commission_inr: number;
  arpu_inr: number;
  avg_order_value_inr: number;
  churned_ordering_customers: number;
  order_customer_churn_rate_pct: number;
  is_partial_month?: boolean;
};

export type CohortRetentionRow = {
  cohort_month: string;
  cohort_size: number;
  month_offset: number;
  retained_customers: number;
  retention_pct: number;
  is_observable: boolean;
};

export type CohortRevenueRow = {
  cohort_month: string;
  month_offset: number;
  orders: number;
  paying_customers: number;
  gmv_inr: number;
  volume_mt: number;
  cumulative_gmv_inr: number;
};

export type InquiryRetentionRow = {
  cohort_month: string;
  cohort_size: number;
  month_offset: number;
  inquiring_customers: number;
  inquiry_retention_pct: number;
  is_observable: boolean;
};

export type BusinessCohortActivityRow = {
  cohort_month: string;
  cohort_size: number;
  month_offset: number;
  active_customers: number;
  activity_pct: number;
  is_observable: boolean;
};

export type InvestorSegment = {
  segment: string;
  customers?: number;
  paying_customers: number;
  repeat_paying_buyers?: number;
  repeat_buyer_rate_pct?: number;
  orders?: number;
  gmv_inr: number;
  volume_mt?: number;
};

export type InvestorCohortResponse = {
  definitions: Record<string, string | string[]>;
  filters: {
    from_date: string;
    to_date: string;
    max_month_offset: number;
    retention_horizon?: string;
  };
  summary: {
    snapshot_month: string | null;
    total_customers: number;
    monthly_paying_buyers: number;
    repeat_paying_buyers: number;
    repeat_buyer_rate_pct: number;
    gmv_inr: number;
    order_count: number;
    volume_mt: number;
    commission_inr: number;
    is_partial_month: boolean;
  };
  monthly: InvestorMonthlyMetric[];
  retention: CohortRetentionRow[];
  retention_milestones: Array<{
    month_offset: number;
    cohort_customers: number;
    retained_customers: number;
    retention_pct: number | null;
    cohorts_included: number;
  }>;
  revenue: CohortRevenueRow[];
  inquiry_retention: InquiryRetentionRow[];
  first_inquiry_order_retention: BusinessCohortActivityRow[];
  first_order_repeat_retention: BusinessCohortActivityRow[];
  segments: {
    buyer_state: InvestorSegment[];
    business_type: InvestorSegment[];
    product_category: InvestorSegment[];
    acquisition_source: InvestorSegment[];
  };
  engagement: {
    available: boolean;
    monthly: Array<{
      month: string;
      mau: number;
      avg_dau: number;
      dau_mau_ratio_percent: number;
      is_partial_month?: boolean;
    }>;
    dormancy: {
      active_30d: number;
      dormant_90d: number;
      tracked_customers: number;
    };
  };
  source_freshness: {
    commercial: Record<string, string | null>;
    engagement: { data_through: string | null; last_updated_at: string | null };
  };
  data_through: string | null;
  last_updated_at: string | null;
};

export const fetchInvestorCohorts = async (params: {
  from_date: string;
  to_date: string;
  max_month_offset: number;
}): Promise<InvestorCohortResponse> => {
  const response = await axios.get(
    `${getAdminApiBaseUrl()}/customers/cohorts-segmentation`,
    { params }
  );
  return response.data;
};
